package com.evoapps.evoshift.data.mapper

import com.evoapps.evoshift.data.dto.auth.DirectEngagementDto
import com.evoapps.evoshift.data.dto.auth.EngagementAccessDto
import com.evoapps.evoshift.data.dto.auth.EngagementDto
import com.evoapps.evoshift.domain.model.AccessLevel
import com.evoapps.evoshift.domain.model.EngagementAccess
import com.evoapps.evoshift.domain.model.Feature
import com.evoapps.evoshift.domain.model.DirectEngagement
import com.evoapps.evoshift.domain.model.GroupGrant
import com.evoapps.evoshift.domain.model.Competence
import com.evoapps.evoshift.domain.model.Engagement
import com.evoapps.evoshift.domain.model.UserPermission

class EngagementMapper {

    // Z DTO (Baza) -> DOMENA (Aplikacja)
    fun toDomain(dto: EngagementDto, workplaceId: String): Engagement {
        return Engagement(
            workplaceId = workplaceId,
            // Jeśli w bazie nie ma nazwy, wstawiamy pusty string lub null (zależy od Twojej logiki UI)
            cachedWorkplaceName = dto.workplaceName,

            role = dto.role,
            // Jeśli lista skills jest null w bazie (starsze wersje), dajemy pustą listę
            skills = dto.skills,

            // Konwersja String -> Enum (bezpieczna, z fallbackiem)
            permission = permissionOf(dto.permission),

            isActive = dto.isActive,
            startDate = dto.startDate ?: "", // Obsługa nulli dla dat
            endDate = dto.endDate,
            isPrimary = dto.isPrimary,
            competence = Competence.entries.firstOrNull { it.name == dto.competence } ?: Competence.INDEPENDENT,
            groupGrant = dto.viaGroupId?.takeIf { it.isNotBlank() }?.let { groupId ->
                GroupGrant(
                    groupId = groupId,
                    groupName = dto.viaGroupName.orEmpty(),
                    permission = permissionOf(dto.groupPermission ?: dto.permission),
                    direct = dto.direct?.let { DirectEngagement(permissionOf(it.permission), it.isActive, it.endDate) }
                )
            },
            formerGroupId = dto.formerGroupId,
            access = dto.access?.takeIf { it.roleId.isNotBlank() }?.let(::accessOf),
            storedPermission = dto.permission.takeIf { it != permissionOf(dto.permission).name }
        )
    }

    // Z DOMENY (Aplikacja) -> DTO (Baza)
    fun toDto(domain: Engagement): EngagementDto {
        return EngagementDto(
            role = domain.role,
            workplaceName = domain.cachedWorkplaceName,
            skills = domain.skills,

            // Konwersja Enum -> String
            permission = domain.permission.name,

            isActive = domain.isActive,
            startDate = domain.startDate,
            endDate = domain.endDate,
            isPrimary = domain.isPrimary,
            competence = domain.competence.name,
            viaGroupId = domain.groupGrant?.groupId,
            viaGroupName = domain.groupGrant?.groupName,
            groupPermission = domain.groupGrant?.permission?.name,
            direct = domain.groupGrant?.direct?.let { DirectEngagementDto(it.permission.name, it.isActive, it.endDate) },
            formerGroupId = domain.formerGroupId,
            // SH-270: never written by the app (the rules accept null as dropping it, the function puts it back).
            access = null
        )
    }

    companion object {
        /** SH-270: unknown features are left out (a newer registry), unknown levels read as none. */
        private fun levelsOf(map: Map<String, Int>): Map<Feature, AccessLevel> =
            map.mapNotNull { (id, level) -> Feature.ofId(id)?.let { it to AccessLevel.of(level) } }.toMap()

        fun accessOf(dto: EngagementAccessDto): EngagementAccess = EngagementAccess(
            roleId = dto.roleId,
            roleName = dto.roleName,
            overrides = levelsOf(dto.overrides),
            grants = dto.grants.mapValues { (_, levels) -> levelsOf(levels) }
        )

        /**
         * The stored permission name. Older documents say MANAGER_OPERATIONAL for an operational
         * manager, which firestore.rules (and the Cloud Functions) treat as MANAGER: the app does
         * too, so such a manager is shown their screens. Unknown names never grant more than
         * employee.
         */
        fun permissionOf(name: String): UserPermission = when (name) {
            "MANAGER_OPERATIONAL" -> UserPermission.MANAGER
            else -> UserPermission.entries.firstOrNull { it.name == name } ?: UserPermission.EMPLOYEE
        }
    }
}
